/**
 * supabaseDB.js — Supabase-backed implementation of the window.api interface.
 * Implements the 44+ method window.api interface for all data operations.
 * Drop-in replacement: zero changes needed in Redux slices or components.
 */

import { supabase } from './supabaseClient';
import { betriebId, speicherPfad } from './betrieb';
import { materialPreiseAngleichen, zuschlagsprofilAusCache } from './kalkulation';
import { transcribeAudio as whisperTranscribe, generateOfferFromTranscript, extractPostenFromBegehung, refineTitleAndDescription as refineText } from './openai';

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Stundensätze/Materialaufschlag: Lohngruppen und Zuschlagsprofil, nicht settings.kalkulation
const KALKULATION_DEFAULTS = {
    taxRate: 19,
    kmRate: 0.90,
};

const COMPANY_DEFAULTS = {
    name: '',
    inhaber: '',
    street: '',
    zip: '',
    city: '',
    phone: '',
    fax: '',
    email: '',
    website: '',
    steuernummer: '',
    ustid: '',
    iban: '',
    bic: '',
    bank: '',
    logo: '',
};

// Onboarding-Status. `completed` entscheidet, ob der Wizard beim Start erscheint.
const ONBOARDING_DEFAULTS = {
    completed: false,
    step: 0,
    completed_at: null,
    skipped: false,
    template_choice: null,   // 'uebernehmen' | 'neu'
    logo_source: null,       // 'upload' | 'extrahiert' | 'platzhalter'
};

// Briefpapier-Template für Angebote und Rechnungen (DIN 5008 als Basis).
const BRIEFPAPIER_DEFAULTS = {
    source: 'din5008',       // 'din5008' | 'uebernommen'
    accentColor: '#1C3557',
    fontFamily: 'Helvetica',
    logoUrl: '',
    logoPosition: 'rechts',  // 'links' | 'mitte' | 'rechts'
    logoWidthMm: 45,
    showFusszeile: true,
    fusszeileSpalten: [],    // bis zu 3 Spalten Fußzeilentext
    kopfzeileText: '',
    anschriftfeldOffsetMm: 45,
    faltmarken: true,
};

const SETTINGS_DEFAULTS = {
    ai_optimization_enabled: 0,
    custom_ai_prompt: '',
    company_name: '',
    currency: 'EUR',
    first_day_of_week: 'Monday',
    working_days_per_week: 5,
    preferred_time_start: '08:00',
    preferred_time_end: '18:00',
    date_format: 'DD.MM.YYYY',
    hour_format: '24 Hour clock',
    time_span_format: '[h]:mm:ss',
    rounding_method: 'none',
    rounding_interval: '5',
    kalkulation: { ...KALKULATION_DEFAULTS },
    workspace_mode: 'demo',
    onboarding: { ...ONBOARDING_DEFAULTS },
    briefpapier: { ...BRIEFPAPIER_DEFAULTS },
};

// ─── Clients ─────────────────────────────────────────────────────────────────

// Felder, die Rechnung und E-Rechnung steuern. Leere Eingaben → NULL statt ''.
function rechnungsfelder(c) {
    const zahl = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
    const ra = c.rechnungsadresse && Object.values(c.rechnungsadresse).some((v) => String(v || '').trim())
        ? c.rechnungsadresse : null;
    return {
        customer_type: c.customer_type === 'firma' ? 'firma' : 'privat',
        kundentyp: ['privat', 'gewerblich', 'oeffentlich'].includes(c.kundentyp) ? c.kundentyp : 'privat',
        ust_id: c.ust_id || null,
        leitweg_id: c.leitweg_id || null,
        reverse_charge_13b: !!c.reverse_charge_13b,
        zahlungsziel_tage: zahl(c.zahlungsziel_tage),
        skonto_prozent: zahl(c.skonto_prozent),
        skonto_tage: zahl(c.skonto_tage),
        rechnungsadresse: ra,
    };
}

async function getClients() {
    const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('is_deleted', 0);
    if (error) { console.error('getClients:', error.message); return []; }
    return data;
}

async function addClient(client) {
    const { data, error } = await supabase
        .from('clients')
        .insert({
            client_name: client.client_name,
            color: client.color || '#6366f1',
            salutation: client.salutation || '',
            contact_first_name: client.contact_first_name || '',
            contact_last_name: client.contact_last_name || '',
            phone: client.phone || '',
            email: client.email || '',
            website: client.website || '',
            whatsapp: client.whatsapp || '',
            preferred_contact: client.preferred_contact || '',
            street: client.street || '',
            house_number: client.house_number || '',
            zip_code: client.zip_code || '',
            city: client.city || '',
            country: client.country || 'Deutschland',
            notes: client.notes || '',
            customer_number: client.customer_number || null,
            sort_index: client.sort_index || 0,
            ...rechnungsfelder(client),
        })
        .select('id, customer_number')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id, customer_number: data.customer_number };
}

async function updateClient(client) {
    const { error } = await supabase.from('clients').update({
        client_name:       client.client_name,
        color:             client.color             || '#6366f1',
        salutation:        client.salutation        || '',
        contact_first_name: client.contact_first_name || '',
        contact_last_name:  client.contact_last_name  || '',
        phone:             client.phone             || '',
        email:             client.email             || '',
        website:           client.website           || '',
        whatsapp:          client.whatsapp          || '',
        preferred_contact: client.preferred_contact || '',
        street:            client.street            || '',
        house_number:      client.house_number      || '',
        zip_code:          client.zip_code          || '',
        city:              client.city              || '',
        country:           client.country           || 'Deutschland',
        notes:             client.notes             || '',
        customer_number:   client.customer_number   || '',
        sort_index:        client.sort_index        || 0,
        ...rechnungsfelder(client),
    }).eq('id', client.id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteClient(clientId) {
    const { error } = await supabase.from('clients').update({ is_deleted: 1 }).eq('id', clientId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function archiveClient(clientId) {
    const { data } = await supabase.from('clients').select('is_archived').eq('id', clientId).single();
    const { error } = await supabase.from('clients').update({ is_archived: data?.is_archived ? 0 : 1 }).eq('id', clientId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function updateClientOrder(clients) {
    const updates = clients.map((c, i) =>
        supabase.from('clients').update({ sort_index: i }).eq('id', c.id)
    );
    await Promise.all(updates);
    return { success: true };
}

// ─── Projects ────────────────────────────────────────────────────────────────

async function getProjects() {
    const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('is_deleted', 0)
        .order('sort_index', { ascending: true });
    if (error) { console.error('getProjects:', error.message); return []; }
    return data;
}

async function addProject(project) {
    const { data, error } = await supabase
        .from('projects')
        .insert({
            project_name: project.project_name,
            color: project.color || '#FFD700',
            hourly_rate: project.hourly_rate || 0,
            estimated_time: project.estimated_time || 0,
            client_id: project.client_id || null,
            billing_type: project.billing_type || 'non_billable',
            description: project.description || '',
            status: project.status || 'geplant',
            start_date: project.start_date || '',
            end_date: project.end_date || '',
            sort_index: project.sort_index || 0,
            baustelle_strasse: project.baustelle_strasse || null,
            baustelle_plz: project.baustelle_plz || null,
            baustelle_ort: project.baustelle_ort || null,
        })
        .select('id')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id };
}

async function updateProject(project) {
    const { id, ...updates } = project;
    const { error } = await supabase.from('projects').update(updates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteProject(projectId) {
    const { error } = await supabase.from('projects').update({ is_deleted: 1 }).eq('id', projectId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function archiveProject(projectId) {
    const { data } = await supabase.from('projects').select('is_archived').eq('id', projectId).single();
    const { error } = await supabase.from('projects').update({ is_archived: data?.is_archived ? 0 : 1 }).eq('id', projectId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function updateProjectOrder(projects) {
    const updates = projects.map((p, i) =>
        supabase.from('projects').update({ sort_index: i }).eq('id', p.id)
    );
    await Promise.all(updates);
    return { success: true };
}

// ─── Posten ──────────────────────────────────────────────────────────────────

async function getPosten(projectId) {
    const { data, error } = await supabase
        .from('project_posten')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_deleted', 0)
        .order('sort_index', { ascending: true });
    if (error) { console.error('getPosten:', error.message); return []; }
    return data;
}

async function addPosten(posten) {
    const { data, error } = await supabase
        .from('project_posten')
        .insert({
            project_id: posten.project_id,
            name: posten.name,
            description: posten.description || '',
            sort_index: posten.sort_index || 0,
        })
        .select('id')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id };
}

async function updatePosten(posten) {
    const { id, ...updates } = posten;
    const { error } = await supabase.from('project_posten').update(updates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deletePosten(postenId) {
    const { error } = await supabase.from('project_posten').update({ is_deleted: 1 }).eq('id', postenId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Tasks ───────────────────────────────────────────────────────────────────

async function getTasks(projectId) {
    const { data, error } = await supabase
        .from('project_tasks')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_deleted', 0)
        .order('posten_id', { ascending: true })
        .order('sort_index', { ascending: true });
    if (error) { console.error('getTasks:', error.message); return []; }
    return data;
}

async function addTask(task) {
    const row = {
        posten_id: task.posten_id,
        project_id: task.project_id,
        name: task.name,
        description: task.description || '',
        estimated_hours: task.estimated_hours || 0,
        status: task.status || 'offen',
        sort_index: task.sort_index || 0,
        position_typ: task.position_typ || 'standard',
    };
    // Optionale Spalten. Fehlt eine davon in der Instanz, darf NICHT die ganze
    // Tätigkeit verloren gehen — genau das ist am 2026-09-01 passiert:
    // `menge_unit` war nie migriert, jeder Task-Insert scheiterte mit 42703,
    // und das Projekt bekam Material ohne eine einzige Arbeitsstunde.
    const optional = {};
    if (task.menge_plan > 0) optional.menge_plan = task.menge_plan;
    if (task.menge_unit)     optional.menge_unit = task.menge_unit;
    if (task.lohngruppe_id)  optional.lohngruppe_id = task.lohngruppe_id;
    // Gruppe im Angebotsstil des Betriebs (posten_id ist der Leistungsbereich)
    if (task.betriebs_gruppe) optional.betriebs_gruppe = task.betriebs_gruppe;
    // Aus dem Leistungskatalog: Herkunft + Snapshot der Bestandteile/des Festpreises
    if (task.leistung_id)    optional.leistung_id = task.leistung_id;
    if (task.kalkulation)    optional.kalkulation = task.kalkulation;
    // Herleitung der Schätzung (Menge × Ansatz, Quelle, getroffene Annahmen).
    if (task.annahmen && Object.keys(task.annahmen).length > 0) optional.annahmen = task.annahmen;
    // Angebotsposition, zu der diese Arbeit gehört (Zeiten je Position)
    if (task.position_uid)   optional.position_uid = task.position_uid;

    const insert = (payload) => supabase
        .from('project_tasks')
        .insert(payload)
        .select('id')
        .single();

    let { data, error } = await insert({ ...row, ...optional });

    // 42703 = undefined_column. Kerndaten sind wichtiger als die Herleitung:
    // zweiter Versuch ohne die optionalen Felder, mit deutlicher Warnung.
    if (error?.code === '42703' && Object.keys(optional).length > 0) {
        console.warn(
            `addTask: Spalte fehlt (${error.message}). Tätigkeit wird ohne ` +
            `${Object.keys(optional).join(', ')} gespeichert — bitte Migration nachziehen.`
        );
        ({ data, error } = await insert(row));
        if (!error) return { success: true, id: data.id, degraded: true };
    }

    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id };
}

async function updateTask(task) {
    const { id, ...updates } = task;
    const { error } = await supabase.from('project_tasks').update(updates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteTask(taskId) {
    const { error } = await supabase.from('project_tasks').update({ is_deleted: 1 }).eq('id', taskId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Posten Material ─────────────────────────────────────────────────────────

async function getPostenMaterial(projectId) {
    const { data, error } = await supabase
        .from('posten_material')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_deleted', 0)
        .order('posten_id', { ascending: true })
        .order('sort_index', { ascending: true });
    if (error) { console.error('getPostenMaterial:', error.message); return []; }
    return data;
}

async function addPostenMaterial(neu) {
    // VK, EK und Zuschlag passen immer zusammen — wie der DB-Trigger material_preis_ableiten
    const item = materialPreiseAngleichen(neu, null, zuschlagsprofilAusCache());
    const row = {
        posten_id: item.posten_id,
        project_id: item.project_id,
        name: item.name,
        menge: item.menge ?? 1,
        einheit: item.einheit || 'Stk.',
        einzelpreis: item.einzelpreis ?? 0,
        type: item.type || 'material',
        status: item.status || 'offen',
        sort_index: item.sort_index || 0,
        position_typ: item.position_typ || 'standard',
    };
    // Herleitung (Mengenberechnung inkl. Verschnitt, Preisquelle) — siehe addTask.
    if (item.annahmen && Object.keys(item.annahmen).length > 0) row.annahmen = item.annahmen;
    // EK und Zuschlag getrennt vom VK (einzelpreis), sobald der EK bekannt ist
    if (item.ek_einzelpreis !== undefined && item.ek_einzelpreis !== null) row.ek_einzelpreis = item.ek_einzelpreis;
    if (item.zuschlag_prozent !== undefined && item.zuschlag_prozent !== null) row.zuschlag_prozent = item.zuschlag_prozent;
    if (item.kostenart) row.kostenart = item.kostenart;
    if (item.betriebs_gruppe) row.betriebs_gruppe = item.betriebs_gruppe;
    const { data, error } = await supabase
        .from('posten_material')
        .insert(row)
        .select('*')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id, row: data };
}

async function updatePostenMaterial(item) {
    const { id } = item;
    // Gespeicherten Stand holen: nur so ist klar, ob VK oder EK/Zuschlag geändert wurde
    const { data: alt } = await supabase.from('posten_material')
        .select('ek_einzelpreis, zuschlag_prozent, einzelpreis').eq('id', id).maybeSingle();
    // Teil-Updates (z. B. Rückgängig mit nur { einzelpreis }) gegen den gespeicherten Stand prüfen
    const { id: _id, ...updates } = materialPreiseAngleichen({ ...(alt || {}), ...item }, alt, zuschlagsprofilAusCache());
    const { data, error } = await supabase.from('posten_material').update(updates).eq('id', id).select('*').maybeSingle();
    if (error) return { success: false, error: error.message };
    return { success: true, row: data };
}

async function deletePostenMaterial(itemId) {
    const { error } = await supabase.from('posten_material').update({ is_deleted: 1 }).eq('id', itemId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Events ──────────────────────────────────────────────────────────────────

async function getEvents() {
    const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('is_deleted', 0);
    if (error) { console.error('getEvents:', error.message); return []; }
    return data;
}

async function addEvent(event) {
    const { data, error } = await supabase
        .from('events')
        .insert({
            subject: event.subject,
            description: event.description || '',
            start_time: event.start_time,
            end_time: event.end_time,
            project_id: event.project_id || null,
            color: event.color || '#FFD700',
            is_scheduled: event.is_scheduled || 0,
            has_break: event.has_break || 0,
            break_start: event.break_start || null,
            break_end: event.break_end || null,
            task_id: event.task_id || null,
        })
        .select('id')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id };
}

async function updateEvent(event) {
    const { id, ...updates } = event;
    const { error } = await supabase.from('events').update(updates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteEvent(eventId) {
    const { error } = await supabase.from('events').update({ is_deleted: 1 }).eq('id', eventId);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Settings ────────────────────────────────────────────────────────────────

// Eine Settings-Zeile je Betrieb — RLS liefert genau die des aktiven Betriebs.
async function getSettings() {
    const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('betrieb_id', betriebId())
        .maybeSingle();
    if (error || !data) return SETTINGS_DEFAULTS;
    return {
        ...SETTINGS_DEFAULTS,
        ...data,
        kalkulation: { ...KALKULATION_DEFAULTS,  ...(data.kalkulation || {}) },
        company:     { ...COMPANY_DEFAULTS,      ...(data.company     || {}) },
        onboarding:  { ...ONBOARDING_DEFAULTS,   ...(data.onboarding  || {}) },
        briefpapier: { ...BRIEFPAPIER_DEFAULTS,  ...(data.briefpapier || {}) },
        workspace_mode: data.workspace_mode || 'demo',
    };
}

async function saveSettings(settings) {
    const { id: _id, betrieb_id: _b, ...felder } = settings || {};
    const { error } = await supabase
        .from('settings')
        .upsert({ ...felder, betrieb_id: betriebId() }, { onConflict: 'betrieb_id' });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Begehungen ──────────────────────────────────────────────────────────────

async function getBegehungen(projectId) {
    const { data, error } = await supabase
        .from('begehungen')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_deleted', 0)
        .order('created_at', { ascending: false });
    if (error) { console.error('getBegehungen:', error.message); return []; }
    return data;
}

async function addBegehung(b) {
    const { error } = await supabase
        .from('begehungen')
        .insert({
            id: b.id,
            project_id: b.project_id,
            title: b.title || 'Begehung',
            status: b.status || 'aktiv',
            notes: b.notes || '',
            extraction_status: b.extraction_status || 'none',
        });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function updateBegehung(b) {
    const { id, ...updates } = b;
    const { error } = await supabase.from('begehungen').update(updates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteBegehung(id) {
    const { error } = await supabase.from('begehungen').update({ is_deleted: 1 }).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Abschnitte ──────────────────────────────────────────────────────────────

async function getAbschnitte(begehungId) {
    const { data, error } = await supabase
        .from('begehung_abschnitte')
        .select('*')
        .eq('begehung_id', begehungId)
        .eq('is_deleted', 0)
        .order('sort_index', { ascending: true });
    if (error) { console.error('getAbschnitte:', error.message); return []; }
    return data;
}

async function addAbschnitt(a) {
    const { error } = await supabase
        .from('begehung_abschnitte')
        .insert({
            id: a.id,
            begehung_id: a.begehung_id,
            project_id: a.project_id,
            name: a.name,
            sort_index: a.sort_index || 0,
            notes: a.notes || '',
        });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function updateAbschnitt(a) {
    const { id, ...updates } = a;
    const { error } = await supabase.from('begehung_abschnitte').update(updates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteAbschnitt(id) {
    const { error } = await supabase.from('begehung_abschnitte').update({ is_deleted: 1 }).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Recordings ──────────────────────────────────────────────────────────────

async function getRecordingsByBegehung(begehungId) {
    const { data, error } = await supabase
        .from('begehung_recordings')
        .select('*')
        .eq('begehung_id', begehungId)
        .eq('is_deleted', 0);
    if (error) { console.error('getRecordingsByBegehung:', error.message); return []; }
    return data;
}

async function addRecording(r) {
    const { error } = await supabase
        .from('begehung_recordings')
        .insert({
            id: r.id,
            abschnitt_id: r.abschnitt_id,
            begehung_id: r.begehung_id,
            file_path: r.file_path,
            duration_seconds: r.duration_seconds || 0,
            transcript: r.transcript || '',
            transcript_status: r.transcript_status || 'pending',
        });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteRecording(id) {
    const { error } = await supabase.from('begehung_recordings').update({ is_deleted: 1 }).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Photos ──────────────────────────────────────────────────────────────────

async function getPhotosByProject(projectId) {
    const { data, error } = await supabase.rpc('get_photos_by_project', { p_project_id: projectId });
    if (error) { console.error('getPhotosByProject:', error.message); return []; }
    return data;
}

async function getPhotosByAbschnitt(abschnittId) {
    const { data, error } = await supabase
        .from('begehung_photos')
        .select('*')
        .eq('abschnitt_id', abschnittId)
        .eq('is_deleted', 0);
    if (error) { console.error('getPhotosByAbschnitt:', error.message); return []; }
    return data;
}

async function addPhoto(p) {
    const { error } = await supabase
        .from('begehung_photos')
        .insert({
            id: p.id,
            abschnitt_id: p.abschnitt_id,
            begehung_id: p.begehung_id,
            project_id: p.project_id || null,
            file_path: p.file_path,
            thumbnail_path: p.thumbnail_path || '',
            caption: p.caption || '',
        });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function updatePhotoCaption({ id, caption }) {
    const { error } = await supabase.from('begehung_photos').update({ caption }).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deletePhoto(id) {
    const { error } = await supabase.from('begehung_photos').update({ is_deleted: 1 }).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Aufmasse ────────────────────────────────────────────────────────────────

async function getAufmasseByProject(projectId) {
    const { data, error } = await supabase.rpc('get_aufmasse_by_project', { p_project_id: projectId });
    if (error) { console.error('getAufmasseByProject:', error.message); return []; }
    return data;
}

// Gescannte Räume mit Geometrie (iOS-App) — für 3D-Modell und Grundriss
async function getAufmassRaeume(projectId) {
    const { data, error } = await supabase
        .from('begehung_abschnitte')
        .select('id, name, notes, geometrie, created_at')
        .eq('project_id', projectId)
        .eq('is_deleted', 0)
        .not('geometrie', 'is', null)
        .order('created_at', { ascending: true });
    if (error) { console.error('getAufmassRaeume:', error.message); return []; }
    return data || [];
}

// Gesprochene Notizen zum Aufmaß (iOS-App, nur Text) mit Raumnamen
async function getAufmassNotizen(projectId) {
    const { data: begehungen } = await supabase
        .from('begehungen').select('id').eq('project_id', projectId).eq('is_deleted', 0);
    const ids = (begehungen || []).map((b) => b.id);
    if (!ids.length) return [];
    const [{ data: notizen, error }, { data: abschnitte }] = await Promise.all([
        supabase.from('begehung_recordings')
            .select('id, begehung_id, abschnitt_id, transcript, created_at')
            .in('begehung_id', ids).eq('is_deleted', 0).neq('transcript', '')
            .order('created_at', { ascending: true }),
        supabase.from('begehung_abschnitte').select('id, name').in('begehung_id', ids).eq('is_deleted', 0),
    ]);
    if (error) { console.error('getAufmassNotizen:', error.message); return []; }
    const namen = Object.fromEntries((abschnitte || []).map((a) => [a.id, a.name]));
    return (notizen || []).map((n) => ({ ...n, abschnitt_name: namen[n.abschnitt_id] || null }));
}

// Von Hand eingegebene Maße hängen wie die vom Handy an der Begehung „Aufmaß“, Abschnitt „Manuell“
// (ohne Geometrie, taucht also nicht als gescannter Raum auf). Fehlt eins, wird es angelegt.
async function manuellerAbschnitt(projectId) {
    const { data: begehungen, error: fehlerB } = await supabase
        .from('begehungen').select('id')
        .eq('project_id', projectId).eq('title', 'Aufmaß').eq('is_deleted', 0)
        .order('created_at', { ascending: false }).limit(1);
    if (fehlerB) throw fehlerB;
    let begehungId = begehungen?.[0]?.id;
    if (!begehungId) {
        begehungId = crypto.randomUUID();
        const res = await addBegehung({ id: begehungId, project_id: projectId, title: 'Aufmaß' });
        if (!res.success) throw new Error(res.error);
    }

    const { data: abschnitte, error: fehlerA } = await supabase
        .from('begehung_abschnitte').select('id')
        .eq('begehung_id', begehungId).eq('name', 'Manuell').eq('is_deleted', 0).is('geometrie', null).limit(1);
    if (fehlerA) throw fehlerA;
    let abschnittId = abschnitte?.[0]?.id;
    if (!abschnittId) {
        abschnittId = crypto.randomUUID();
        const res = await addAbschnitt({ id: abschnittId, begehung_id: begehungId, project_id: projectId, name: 'Manuell', sort_index: 999 });
        if (!res.success) throw new Error(res.error);
    }
    return { begehungId, abschnittId };
}

async function addAufmass(m) {
    let { abschnitt_id, begehung_id } = m;
    if (!abschnitt_id || !begehung_id) {
        try {
            ({ abschnittId: abschnitt_id, begehungId: begehung_id } = await manuellerAbschnitt(m.project_id));
        } catch (e) {
            return { success: false, error: e.message };
        }
    }
    const { error } = await supabase
        .from('begehung_aufmasse')
        .insert({
            id: m.id,
            abschnitt_id,
            begehung_id,
            project_id: m.project_id,
            label: m.label,
            value: m.value || 0,
            unit: m.unit || 'm',
            notes: m.notes || '',
        });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function updateAufmass(m) {
    const { id, ...updates } = m;
    const { error } = await supabase.from('begehung_aufmasse').update(updates).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteAufmass(id) {
    const { error } = await supabase.from('begehung_aufmasse').update({ is_deleted: 1 }).eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Notizen ─────────────────────────────────────────────────────────────────

async function getNotiz(projectId) {
    const { data, error } = await supabase
        .from('project_notizen')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_deleted', 0)
        .single();
    if (error) return null;
    return data;
}

async function saveNotiz({ id, project_id, content }) {
    const { error } = await supabase
        .from('project_notizen')
        .upsert({ id, project_id, content }, { onConflict: 'project_id' });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── AI (via Supabase Edge Function — server-side) ──────────────────────────

async function transcribeAudio(audioData, mimeType) {
    try {
        const blob = new Blob([audioData], { type: mimeType || 'audio/webm' });
        const text = await whisperTranscribe(blob);
        return { text };
    } catch (e) {
        return { error: e.message };
    }
}

async function generateOffer(transcript) {
    try {
        return await generateOfferFromTranscript(transcript);
    } catch (e) {
        return { error: e.message };
    }
}

async function refineTitleAndDescription({ subject, description, customPrompt, type }) {
    try {
        const result = await refineText({ title: subject, description, prompt: customPrompt, type });
        if (typeof result === 'string') {
            return type === 'title' ? { subject: result } : { description: result };
        }
        return result;
    } catch (e) {
        return { error: e.message };
    }
}

// ─── Transcription & Extraction ──────────────────────────────────────────────

async function transcribeBegehung(begehungId) {
    const { data: abschnitte } = await supabase
        .from('begehung_abschnitte')
        .select('*')
        .eq('begehung_id', begehungId)
        .eq('is_deleted', 0)
        .order('sort_index', { ascending: true });

    const { data: allAufmasse } = await supabase
        .from('begehung_aufmasse')
        .select('*')
        .eq('begehung_id', begehungId)
        .eq('is_deleted', 0);

    const { data: allRecordings } = await supabase
        .from('begehung_recordings')
        .select('*')
        .eq('begehung_id', begehungId)
        .eq('is_deleted', 0);

    // Transcribe audio recordings that don't have transcripts yet
    for (const rec of allRecordings || []) {
        if (rec.transcript) continue;
        try {
            // Extract storage path from the full URL
            const storagePath = decodeURIComponent(
                rec.file_path.replace(/^.*\/storage\/v1\/object\/(?:public|sign)\/recordings\//, '').split('?')[0]
            );
            const { data: audioBlob, error } = await supabase.storage
                .from('recordings')
                .download(storagePath);
            if (error) throw error;
            if (audioBlob) {
                const transcript = await whisperTranscribe(audioBlob);
                rec.transcript = transcript;
                await supabase
                    .from('begehung_recordings')
                    .update({ transcript, transcript_status: 'completed' })
                    .eq('id', rec.id);
            }
        } catch (e) {
            console.warn('Whisper transcription failed for recording', rec.id, e.message);
        }
    }

    // Notizen ohne Raum (iOS-App) als eigener Abschnitt
    const allgemein = (allRecordings || []).filter(r => !r.abschnitt_id && r.transcript).map(r => r.transcript);
    if (!abschnitte?.length && !allgemein.length) {
        return { success: false, error: 'Keine Abschnitte in dieser Begehung vorhanden.' };
    }

    // Build consolidation object
    const consolidation = {
        sections: [
            ...(abschnitte || []).map(a => ({
                name: a.name,
                transcripts: (allRecordings || [])
                    .filter(r => r.abschnitt_id === a.id && r.transcript)
                    .map(r => r.transcript),
                measurements: (allAufmasse || [])
                    .filter(m => m.abschnitt_id === a.id)
                    .map(m => `${m.label}: ${m.value}${m.unit}`),
            })),
            ...(allgemein.length ? [{ name: 'Allgemein', transcripts: allgemein, measurements: [] }] : []),
        ]
    };

    const result = await extractPostenFromBegehung(consolidation);

    if (result.error) {
        return { success: false, error: result.error };
    }

    const extracted = {
        posten: (result.posten || []).map(p => ({
            ...p,
            id: crypto.randomUUID(),
            tasks: (p.tasks || []).map(t => ({ ...t, id: crypto.randomUUID() })),
        })),
        unclassified: result.unclassified || [],
    };

    return { success: true, extracted };
}

// ─── File saving (Supabase Storage) ──────────────────────────────────────────

async function savePhotoFile({ id, arrayBuffer, ext }) {
    try {
        const filePath = speicherPfad(`photo_${id}.${ext || 'jpg'}`);
        const blob = new Blob([arrayBuffer], { type: `image/${ext || 'jpeg'}` });
        const { error } = await supabase.storage
            .from('photos')
            .upload(filePath, blob, { upsert: true });
        if (error) throw error;

        const { data: urlData } = supabase.storage
            .from('photos')
            .getPublicUrl(filePath);

        return { success: true, filePath: urlData.publicUrl };
    } catch (e) {
        return { success: false, error: e.message };
    }
}

async function saveAudioFile({ id, arrayBuffer, mimeType }) {
    try {
        const ext = mimeType && mimeType.includes('mp4') ? 'mp4' : 'webm';
        const filePath = speicherPfad(`rec_${id}.${ext}`);
        const blob = new Blob([arrayBuffer], { type: mimeType || 'audio/webm' });
        const { error } = await supabase.storage
            .from('recordings')
            .upload(filePath, blob, { upsert: true });
        if (error) throw error;

        // Private bucket → signed URL (1 year)
        const { data: urlData } = await supabase.storage
            .from('recordings')
            .createSignedUrl(filePath, 60 * 60 * 24 * 365);

        return { success: true, filePath: urlData.signedUrl };
    } catch (e) {
        return { success: false, error: e.message };
    }
}

// ─── Onboarding ──────────────────────────────────────────────────────────────

/**
 * Lädt eine Onboarding-Datei (PDF/Bild) in den privaten Bucket 'onboarding'
 * und legt den zugehörigen DB-Datensatz an.
 * @param {File} file
 * @returns {{ success: boolean, doc?: object, error?: string }}
 */
async function uploadOnboardingDocument(file) {
    try {
        const id = crypto.randomUUID();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const filePath = speicherPfad(`${id}_${safeName}`);

        const { error: upErr } = await supabase.storage
            .from('onboarding')
            .upload(filePath, file, { upsert: true, contentType: file.type });
        if (upErr) throw upErr;

        const { data, error } = await supabase
            .from('onboarding_documents')
            .insert({
                id,
                file_name: file.name,
                file_path: filePath,
                mime_type: file.type,
                size_bytes: file.size,
                status: 'hochgeladen',
            })
            .select()
            .single();
        if (error) throw error;

        return { success: true, doc: data };
    } catch (e) {
        return { success: false, error: e.message };
    }
}

async function getOnboardingDocuments() {
    const { data, error } = await supabase
        .from('onboarding_documents')
        .select('*')
        .order('created_at', { ascending: true });
    if (error) return [];
    return data || [];
}

async function updateOnboardingDocument({ id, ...fields }) {
    const { error } = await supabase
        .from('onboarding_documents')
        .update(fields)
        .eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteOnboardingDocument(id) {
    const { data: doc } = await supabase
        .from('onboarding_documents')
        .select('file_path')
        .eq('id', id)
        .single();

    if (doc?.file_path) {
        await supabase.storage.from('onboarding').remove([doc.file_path]);
    }

    const { error } = await supabase.from('onboarding_documents').delete().eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

/**
 * Firmenlogo in den public Bucket 'branding'. Fester Pfad je Betrieb, damit ein
 * neues Logo das alte ersetzt statt Karteileichen zu hinterlassen.
 * Cache-Buster im Rückgabe-URL, sonst zeigt der Browser nach dem Tausch das alte.
 */
async function uploadLogo(file) {
    try {
        const ext = (file.name.split('.').pop() || 'png').toLowerCase();
        const filePath = speicherPfad(`logo.${ext}`);

        const { error } = await supabase.storage
            .from('branding')
            .upload(filePath, file, { upsert: true, contentType: file.type });
        if (error) throw error;

        const { data } = supabase.storage.from('branding').getPublicUrl(filePath);
        return { success: true, url: `${data.publicUrl}?v=${Date.now()}` };
    } catch (e) {
        return { success: false, error: e.message };
    }
}

/** Feste Betriebsunterlage (AGB, Widerrufsbelehrung) in 'branding' — ersetzt die alte Fassung. */
async function uploadBetriebsdatei(file, name) {
    try {
        const ext = (file.name.split('.').pop() || 'pdf').toLowerCase();
        const filePath = speicherPfad(`${name}.${ext}`);

        const { error } = await supabase.storage
            .from('branding')
            .upload(filePath, file, { upsert: true, contentType: file.type });
        if (error) throw error;

        const { data } = supabase.storage.from('branding').getPublicUrl(filePath);
        return { success: true, url: `${data.publicUrl}?v=${Date.now()}` };
    } catch (e) {
        return { success: false, error: e.message };
    }
}

// ─── Entities (AI-First Entity Store) ────────────────────────────────────────

async function addEntity(entity) {
    const { data, error } = await supabase
        .from('entities')
        .insert({
            type: entity.type,
            name: entity.name || null,
            props: entity.props || {},
        })
        .select()
        .single();
    if (error) { console.error('addEntity:', error.message); return null; }
    return data;
}

async function getEntities(type = null) {
    let query = supabase.from('entities').select('*').eq('is_deleted', 0);
    if (type) query = query.eq('type', type);
    query = query.order('created_at', { ascending: false });
    const { data, error } = await query;
    if (error) { console.error('getEntities:', error.message); return []; }
    return data;
}

async function updateEntity(entity) {
    const { error } = await supabase
        .from('entities')
        .update({ name: entity.name, props: entity.props, updated_at: new Date().toISOString() })
        .eq('id', entity.id);
    if (error) { console.error('updateEntity:', error.message); return { success: false }; }
    return { success: true };
}

async function deleteEntity(id) {
    const { error } = await supabase
        .from('entities')
        .update({ is_deleted: 1 })
        .eq('id', id);
    if (error) { console.error('deleteEntity:', error.message); return { success: false }; }
    return { success: true };
}

// ─── Relationships ───────────────────────────────────────────────────────────

async function addRelationship(rel) {
    const { data, error } = await supabase
        .from('relationships')
        .insert({
            source_id: rel.source_id,
            target_id: rel.target_id,
            type: rel.type,
            props: rel.props || {},
        })
        .select()
        .single();
    if (error) { console.error('addRelationship:', error.message); return null; }
    return data;
}

async function getRelationships(entityId) {
    const { data, error } = await supabase
        .from('relationships')
        .select('*')
        .or(`source_id.eq.${entityId},target_id.eq.${entityId}`);
    if (error) { console.error('getRelationships:', error.message); return []; }
    return data;
}

// ─── Interactions (AI interaction log) ───────────────────────────────────────

async function addInteraction(interaction) {
    const { data, error } = await supabase
        .from('interactions')
        .insert({
            input_type: interaction.input_type || 'voice',
            raw_input: interaction.raw_input,
            raw_file_path: interaction.raw_file_path || null,
            ai_output: interaction.ai_output || null,
            entities_created: interaction.entities_created || [],
            confidence: interaction.confidence || null,
            user_confirmed: interaction.user_confirmed || false,
            corrections: interaction.corrections || null,
        })
        .select()
        .single();
    if (error) { console.error('addInteraction:', error.message); return null; }
    return data;
}

async function getInteractions() {
    const { data, error } = await supabase
        .from('interactions')
        .select('*')
        .order('created_at', { ascending: false });
    if (error) { console.error('getInteractions:', error.message); return []; }
    return data;
}

// ─── Mitarbeiter ─────────────────────────────────────────────────────────────

async function getMitarbeiter() {
    const { data, error } = await supabase
        .from('mitarbeiter')
        .select('*')
        .eq('is_deleted', 0)
        .order('name');
    if (error) { console.error('getMitarbeiter:', error.message); return []; }
    return data;
}

// Stammdaten eines Mitarbeiters. "name" bleibt als Anzeigename erhalten
// (Plantafel, Zeiten, WhatsApp) und wird aus Vor- und Nachname gebildet.
function mitarbeiterFelder(m) {
    const name = [m.vorname, m.nachname].filter(Boolean).join(' ').trim() || m.name || '';
    const datum = (v) => v || null;
    return {
        name,
        vorname: m.vorname ?? null,
        nachname: m.nachname ?? null,
        rolle: m.rolle || '',
        color: m.color || '#6366f1',
        phone: m.phone || '',
        email: m.email || null,
        personalnummer: m.personalnummer || null,
        geburtsdatum: datum(m.geburtsdatum),
        beschaeftigungsart: m.beschaeftigungsart || 'vollzeit',
        eintritt: datum(m.eintritt),
        austritt: datum(m.austritt),
        wochenstunden: m.wochenstunden ?? 40,
        arbeitstage: m.arbeitstage?.length ? m.arbeitstage : [1, 2, 3, 4, 5],
        urlaubsanspruch: m.urlaubsanspruch ?? 30,
        qualifikationen: m.qualifikationen || [],
        lohngruppe_id: m.lohngruppe_id || null,
        notizen: m.notizen || null,
    };
}

async function addMitarbeiter(m) {
    const { data, error } = await supabase
        .from('mitarbeiter')
        .insert(mitarbeiterFelder(m))
        .select('id')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id };
}

async function updateMitarbeiter(m) {
    const { error } = await supabase
        .from('mitarbeiter')
        .update(mitarbeiterFelder(m))
        .eq('id', m.id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteMitarbeiter(id) {
    const { error } = await supabase
        .from('mitarbeiter')
        .update({ is_deleted: 1 })
        .eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Fahrzeuge ───────────────────────────────────────────────────────────────

async function getFahrzeuge() {
    const { data, error } = await supabase
        .from('fahrzeuge')
        .select('*')
        .eq('is_deleted', 0)
        .order('name');
    if (error) { console.error('getFahrzeuge:', error.message); return []; }
    return data;
}

// Stammdaten eines Fahrzeugs oder Anhängers. Leere Zahlenfelder = unbekannt,
// dann prüft die Plantafel an der Stelle nichts.
function fahrzeugFelder(f) {
    const zahl = (v) => {
        const n = Number(String(v ?? '').replace(',', '.'));
        return v === '' || v == null || !Number.isFinite(n) || n <= 0 ? null : Math.round(n);
    };
    const anhaenger = f.art === 'anhaenger';
    return {
        name: String(f.name || '').trim(),
        kennzeichen: String(f.kennzeichen || '').trim(),
        color: f.color || '#f97316',
        art: anhaenger ? 'anhaenger' : 'fahrzeug',
        typ: anhaenger ? null : (f.typ || null),
        antrieb: anhaenger ? null : (f.antrieb || null),
        reichweite_km: !anhaenger && f.antrieb === 'elektro' ? zahl(f.reichweite_km) : null,
        sitzplaetze: anhaenger ? null : zahl(f.sitzplaetze),
        zul_gesamtgewicht_kg: zahl(f.zul_gesamtgewicht_kg),
        zuladung_kg: zahl(f.zuladung_kg),
        ladelaenge_cm: zahl(f.ladelaenge_cm),
        anhaengerkupplung: !anhaenger && !!f.anhaengerkupplung,
        anhaengelast_kg: !anhaenger && f.anhaengerkupplung ? zahl(f.anhaengelast_kg) : null,
        gebremst: anhaenger ? (f.gebremst ?? true) : null,
        hu_bis: f.hu_bis || null,
        ausstattung: f.ausstattung || [],
        notizen: f.notizen || null,
    };
}

async function addFahrzeug(f) {
    const { data, error } = await supabase
        .from('fahrzeuge')
        .insert(fahrzeugFelder(f))
        .select('*')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id, fahrzeug: data };
}

async function updateFahrzeug(f) {
    const { error } = await supabase
        .from('fahrzeuge')
        .update(fahrzeugFelder(f))
        .eq('id', f.id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteFahrzeug(id) {
    const { error } = await supabase
        .from('fahrzeuge')
        .update({ is_deleted: 1 })
        .eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Einsätze (Plantafel) ────────────────────────────────────────────────────

async function getEinsaetze() {
    const { data, error } = await supabase
        .from('einsaetze')
        .select('*')
        .or('is_deleted.is.null,is_deleted.eq.0')
        .order('start_date');
    if (error) { console.error('getEinsaetze:', error.message); return []; }
    return data || [];
}

async function addEinsatz(e) {
    const mitarbeiterIds = e.mitarbeiter_ids || (e.mitarbeiter_id ? [e.mitarbeiter_id] : []);
    const payload = {
        project_id:      e.project_id      || null,
        mitarbeiter_id:  mitarbeiterIds[0]  || null,
        fahrzeug_id:     (e.fahrzeug_ids || [])[0] || e.fahrzeug_id || null,
        start_date:      e.start_date,
        end_date:        e.end_date,
        start_time:      e.start_time      || '',
        end_time:        e.end_time        || '',
        notes:           e.notes           || '',
        color:           e.color           || '',
        mitarbeiter_ids: mitarbeiterIds,
        fahrzeug_ids:    e.fahrzeug_ids    || [],
        werkzeug_ids:    e.werkzeug_ids    || [],
        is_team:         mitarbeiterIds.length > 1,
        is_deleted:      0,
    };
    const { data, error } = await supabase
        .from('einsaetze')
        .insert(payload)
        .select('id')
        .single();
    if (error) {
        console.error('[addEinsatz] FAILED:', error.message, 'payload:', payload);
        return { success: false, error: error.message };
    }
    console.log('[addEinsatz] SUCCESS id:', data.id, 'project_id:', payload.project_id);
    return { success: true, id: data.id };
}

async function updateEinsatz(e) {
    const mitarbeiterIds = e.mitarbeiter_ids || (e.mitarbeiter_id ? [e.mitarbeiter_id] : []);
    const { error } = await supabase
        .from('einsaetze')
        .update({
            project_id:      e.project_id      || null,
            mitarbeiter_id:  mitarbeiterIds[0]  || null,
            fahrzeug_id:     (e.fahrzeug_ids || [])[0] || e.fahrzeug_id || null,
            start_date:      e.start_date,
            end_date:        e.end_date,
            start_time:      e.start_time      || '',
            end_time:        e.end_time        || '',
            notes:           e.notes           || '',
            color:           e.color           || '',
            mitarbeiter_ids: mitarbeiterIds,
            fahrzeug_ids:    e.fahrzeug_ids    || [],
            werkzeug_ids:    e.werkzeug_ids    || [],
            is_team:         mitarbeiterIds.length > 1,
        })
        .eq('id', e.id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

async function deleteEinsatz(id) {
    const { error } = await supabase
        .from('einsaetze')
        .update({ is_deleted: 1 })
        .eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Werkzeuge (via entities — type: 'werkzeug') ─────────────────────────────

async function getWerkzeuge() {
    const data = await getEntities('werkzeug');
    // Normalize to flat shape expected by components
    return data.map(e => ({
        id:           e.id,
        name:         e.name,
        beschreibung: e.props?.beschreibung || '',
        kategorie:    e.props?.kategorie    || '',
        is_deleted:   e.is_deleted,
        created_at:   e.created_at,
    }));
}

async function addWerkzeug(w) {
    const entity = await addEntity({
        type:  'werkzeug',
        name:  w.name,
        props: { beschreibung: w.beschreibung || '', kategorie: w.kategorie || '' },
    });
    if (!entity) return { success: false, error: 'Fehler beim Speichern' };
    return { success: true, id: entity.id };
}

async function updateWerkzeug(w) {
    return updateEntity({
        id:    w.id,
        name:  w.name,
        props: { beschreibung: w.beschreibung || '', kategorie: w.kategorie || '' },
    });
}

async function deleteWerkzeug(id) {
    return deleteEntity(id);
}

// ─── HeyOptero Drafts ─────────────────────────────────────────────────────────

async function getDrafts() {
    const { data, error } = await supabase
        .from('optimate_drafts')
        .select('*')
        // in_arbeit: Mobile-Entwurf wird im Hintergrund ausgewertet; fehler: Auswertung gescheitert
        .in('status', ['pending', 'in_arbeit', 'fehler'])
        .order('created_at', { ascending: false });
    if (error) { console.error('getDrafts:', error.message); return []; }
    return data;
}

async function saveDraft({ transcript, zusammenfassung, project_name, entities, rueckfragen, draft_type }) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
        .from('optimate_drafts')
        .insert({
            user_id: user?.id || null,
            transcript: transcript || '',
            zusammenfassung: zusammenfassung || '',
            project_name: project_name || '',
            entities: entities || [],
            rueckfragen: rueckfragen || [],
            draft_type: draft_type || 'angebot',
            status: 'pending',
            device: 'mobile',
        })
        .select('id')
        .single();
    if (error) return { success: false, error: error.message };
    return { success: true, id: data.id };
}

async function updateDraftStatus(id, status) {
    const { error } = await supabase
        .from('optimate_drafts')
        .update({ status })
        .eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// Begehung vom Handy (Räume, Aufmaß, Fotos) ans neu angelegte Projekt hängen
async function begehungProjektZuordnen(begehungId, projectId) {
    const { error } = await supabase.rpc('begehung_projekt_zuordnen', { p_begehung: begehungId, p_project: projectId });
    if (error) return { success: false, error: error.message };
    return { success: true };
}

// ─── Export as window.api-compatible object ──────────────────────────────────

const supabaseDB = {
    // Clients
    getClients,
    addClient,
    updateClient,
    deleteClient,
    archiveClient,
    updateClientOrder,
    // Projects
    getProjects,
    addProject,
    updateProject,
    deleteProject,
    archiveProject,
    updateProjectOrder,
    // Posten
    getPosten,
    addPosten,
    updatePosten,
    deletePosten,
    // Tasks
    getTasks,
    addTask,
    updateTask,
    deleteTask,
    // Posten Material
    getPostenMaterial,
    addPostenMaterial,
    updatePostenMaterial,
    deletePostenMaterial,
    // Events
    getEvents,
    addEvent,
    updateEvent,
    deleteEvent,
    // Settings
    getSettings,
    saveSettings,
    // AI
    transcribeAudio,
    generateOffer,
    refineTitleAndDescription,
    // File saving
    saveAudioFile,
    savePhotoFile,
    // Begehungen
    getBegehungen,
    addBegehung,
    updateBegehung,
    deleteBegehung,
    // Abschnitte
    getAbschnitte,
    addAbschnitt,
    updateAbschnitt,
    deleteAbschnitt,
    // Recordings
    getRecordingsByBegehung,
    addRecording,
    deleteRecording,
    // Photos
    getPhotosByProject,
    getPhotosByAbschnitt,
    addPhoto,
    updatePhotoCaption,
    deletePhoto,
    // Aufmasse
    getAufmasseByProject,
    addAufmass,
    updateAufmass,
    deleteAufmass,
    // Notizen
    getNotiz,
    saveNotiz,
    // Transcription & Extraction
    transcribeBegehung,
    // Entities (AI-First)
    addEntity,
    getEntities,
    updateEntity,
    deleteEntity,
    // Relationships
    addRelationship,
    getRelationships,
    // Interactions
    addInteraction,
    getInteractions,
    // Mitarbeiter
    getMitarbeiter,
    addMitarbeiter,
    updateMitarbeiter,
    deleteMitarbeiter,
    // Fahrzeuge
    getFahrzeuge,
    addFahrzeug,
    updateFahrzeug,
    deleteFahrzeug,
    // Einsätze (Plantafel)
    getEinsaetze,
    addEinsatz,
    updateEinsatz,
    deleteEinsatz,
    // Werkzeuge
    getWerkzeuge,
    addWerkzeug,
    updateWerkzeug,
    deleteWerkzeug,
    // HeyOptero Drafts
    getDrafts,
    saveDraft,
    updateDraftStatus,
    begehungProjektZuordnen,
    getAufmassNotizen,
    getAufmassRaeume,
    // Onboarding
    uploadOnboardingDocument,
    getOnboardingDocuments,
    updateOnboardingDocument,
    deleteOnboardingDocument,
    uploadLogo,
    uploadBetriebsdatei,
};

export default supabaseDB;
